/**
 * NotYouDialog Component
 * Reusable dialog component for "Not You?" functionality with API integration
 */

import React, { useState } from 'react';
import Dialog from './Dialog';
import Button from './Button';
import { employeeService } from '../../services';

interface NotYouDialogProps {
  isOpen: boolean;
  onClose: () => void;
  currentEmail: string;
  onSuccess?: (newEmail: string) => void;
}

const NotYouDialog: React.FC<NotYouDialogProps> = ({
  isOpen,
  onClose,
  currentEmail,
  onSuccess
}) => {
  const [text, setText] = useState('');
  const [textError, setTextError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    setText(value);
    
    // Clear error when user starts typing
    if (textError && value.trim()) {
      setTextError('');
    }
  };

  const handleSubmit = async () => {
    const trimmedText = text.trim();
    
    // Validation - only check for blank
    if (!trimmedText) {
      setTextError('This field is required');
      return;
    }

    setIsSubmitting(true);
    setTextError('');

    try {
      // Call the API to report NOT YOU issue
      const response = await employeeService.reportIssueWithType(
        'not_you',
        `Current email shown is "${currentEmail}", user provided: "${trimmedText}"`
      );
      
      if (response.status) {
        console.log('Email issue reported successfully:', response.message);
        setShowSuccess(true);
        
        // Call success callback if provided
        if (onSuccess) {
          onSuccess(trimmedText);
        }
      } else {
        console.error('Failed to report issue:', response.error);
        setTextError(response.error || 'Failed to submit request. Please try again.');
      }
    } catch (error) {
      console.error('Error reporting issue:', error);
      setTextError('Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset all state when closing
    setText('');
    setTextError('');
    setIsSubmitting(false);
    setShowSuccess(false);
    onClose();
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !isSubmitting && text.trim() && !textError) {
      handleSubmit();
    }
  };

  if (showSuccess) {
    return (
      <Dialog
        isOpen={isOpen}
        onClose={handleClose}
        type="success"
        title="Email Submitted"
        message="Your email has been submitted successfully. Our team will verify your query and revert back to you as soon as possible."
        confirmText="OK"
        onConfirm={handleClose}
        showCancel={false}
        size="extended"
      />
    );
  }


  return (
    <Dialog
      isOpen={isOpen}
      onClose={!isSubmitting ? handleClose : () => {}}
      type="info"
      title="Not You?"
      size="extended"
      message={
        <div className="space-y-4">
          <p className="text-gray-600 text-sm">
            Please provide your correct information so that we can verify and update our system.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 ">
              Your Information
            </label>
            <textarea
              value={text}
              onChange={handleTextChange}
              onKeyDown={handleKeyPress}
              placeholder="Enter your correct email address or any relevant information"
              disabled={isSubmitting}
              autoFocus
              rows={3}
              className={`w-full p-3 border rounded-lg outline-none text-sm ${
                textError ? 'border-red-500' : 'border-gray-300'
              } ${isSubmitting ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'}`}
            />
            {textError && (
              <p className="text-red-500 text-xs mt-1">{textError}</p>
            )}
          </div>
        </div>
      }
      showCancel={false}
      customActions={
        <div className="flex w-full flex-col-reverse gap-3 pt-4 sm:flex-row sm:justify-end">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !text.trim() || !!textError}
            className={`px-4 py-2 ${
              isSubmitting || !text.trim() || !!textError
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-50 hover:bg-gray-300'
                : 'bg-purple-800 text-white hover:bg-purple-700'
            }`}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </Button>
        </div>
      }
    />
  );
};

export default NotYouDialog;
